import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import FloatingButtons from '../components/FloatingButtons/FloatingButtons';
import ProductCard from '../components/Products/ProductCard';
import { newArrivals, featuredCollection, specialOne, highlights } from '../data/products';
import { categoryProductMap } from '../data/categoryProducts';

const CategoryPage = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);

  // Get all products from all collections
  const allProducts = [
    ...newArrivals,
    ...featuredCollection,
    ...specialOne,
    ...highlights.birthday,
    ...highlights.anniversary,
    ...highlights.babyShower,
    ...highlights.gifting
  ];

  // Filter products based on category
  const getFilteredProducts = () => {
    if (!category) return allProducts;

    const categoryLower = category.toLowerCase();

    // Dedicated product lists for specific category pages
    if (categoryProductMap[categoryLower]) {
      return categoryProductMap[categoryLower];
    }

    const titleLower = categoryLower.replace(/-/g, ' ');

    const categoryKeywords = {
      'christmas-decoration': ['christmas', 'xmas', 'holiday', 'festive'],
      'birthday-decoration': ['birthday', 'bday', 'candyland', 'rainbow', 'balloon'],
      'pre-wedding-decoration': ['pre-wedding', 'pre wedding', 'proposal', 'engagement', 'ring', 'wedding'],
      'anniversary-decoration': ['anniversary', 'mr & mrs', 'love you'],
      'occasion-decoration': ['occasion', 'celebration', 'party', 'romantic'],
      'kids-birthday-decoration': ['kids', '1st birthday', 'hello kitty', 'astronaut', 'candyland', 'rainbow', 'birthday'],
      'car-boot-decoration': ['car boot', 'boot decor', 'boot'],
      'diwali-decoration': ['diwali', 'festive', 'festival'],
      'kids-activity': ['kids', 'children', 'activity', 'theme'],
      'pre-wedding': ['pre-wedding', 'pre wedding', 'proposal', 'engagement', 'ring', 'wedding'],
      'birthday': ['birthday', 'bday', 'balloon'],
      'bachelorette': ['bachelorette', 'bachelor', 'bride'],
      'customize': ['custom', 'customize', 'personalized'],
      'festive': ['festive', 'festival', 'diwali', 'christmas'],
      'baby-shower': ['baby shower', 'baby', 'naming ceremony', 'welcome baby', 'pastel'],
      'diwali': ['diwali', 'festive', 'festival'],
      // Spaced keys (fallback)
      'christmas decoration': ['christmas', 'xmas', 'holiday', 'festive'],
      'birthday decoration': ['birthday', 'bday', 'candyland', 'rainbow', 'balloon'],
      'pre wedding decoration': ['pre-wedding', 'pre wedding', 'proposal', 'engagement', 'ring', 'wedding'],
      'anniversary decoration': ['anniversary'],
      'kids birthday decoration': ['kids', '1st birthday', 'hello kitty', 'astronaut', 'birthday'],
      'car boot decoration': ['car boot', 'boot decor', 'boot']
    };

    const keywords =
      categoryKeywords[categoryLower] ||
      categoryKeywords[titleLower] ||
      titleLower.split(' ').filter((word) => word.length > 2);

    return allProducts.filter((product) => {
      const productTitle = product.title.toLowerCase();
      return keywords.some((keyword) => productTitle.includes(keyword));
    });
  };

  const getCategoryTitle = () => {
    const categoryMap = {
      'party-accessories': 'Party Accessories',
      'banners': 'Banners',
      'balloons': 'Balloons',
      'foil-balloons': 'Foil Balloons',
      'christmas-decoration': 'Christmas Decoration',
      'birthday-decoration': 'Birthday Decoration',
      'pre-wedding-decoration': 'Pre-Wedding Decoration',
      'anniversary-decoration': 'Anniversary Decoration',
      'occasion-decoration': 'Occasion Decoration',
      'e-invites': 'E-invites',
      'kids-birthday-decoration': 'Kids Birthday Decoration',
      'car-boot-decoration': 'Car Boot Decoration',
      'diwali-decoration': 'Diwali Decoration',
      'kids-activity': 'Kids Activity',
      'pre-wedding': 'Pre-Wedding Products',
      'birthday': 'Birthday Products',
      'bachelorette': 'Bachelorette Products',
      'customize': 'Customize Products',
      'festive': 'Festive Products',
      'baby-shower': 'Baby Shower Products',
      'diwali': 'Diwali Products'
    };
    
    return categoryMap[category] || category.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const filteredProducts = getFilteredProducts();
  const categoryTitle = getCategoryTitle();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#8B1538] to-[#a01d47] text-white py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              {categoryTitle}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
              Discover our amazing collection of {categoryTitle.toLowerCase()} for your special occasions
            </p>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredProducts.length > 0 ? (
              <>
                <div className="mb-8">
                  <p className="text-gray-600">
                    Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-8">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} layout="grid" />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎈</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  No Products Found
                </h2>
                <p className="text-gray-600 text-lg mb-8">
                  We're currently updating our {categoryTitle.toLowerCase()} collection. Check back soon!
                </p>
                <a
                  href="/"
                  className="inline-block bg-[#8B1538] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#a01d47] transition-colors"
                >
                  Browse Other Products
                </a>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default CategoryPage;

